'use client'

/**
 * AnalyticsLoader
 * ──────────────────────────────────────────────────────────────────────────
 * Client-only loader for PostHog (product analytics) and Sentry (error
 * tracking). Both load from a CDN so the build doesn't need extra npm
 * packages — this keeps the Vercel cold start tight and means we can ship
 * these before a full SDK integration.
 *
 * Both are gated on the respective env vars:
 *   NEXT_PUBLIC_POSTHOG_KEY   — required for PostHog to initialise
 *   NEXT_PUBLIC_POSTHOG_HOST  — optional (defaults to https://app.posthog.com)
 *   NEXT_PUBLIC_SENTRY_DSN    — required for Sentry to initialise
 *
 * If a key is missing the loader silently skips that vendor — critical for
 * local dev + preview envs where we don't want noise.
 *
 * Cookie banner interaction:
 *   - We read a 'na_cookie_consent' cookie (set by CookieBanner in Batch 7).
 *   - If it's missing or set to 'rejected', PostHog is *not* loaded.
 *   - Sentry is always loaded — it's strictly-necessary error tracking
 *     (no PII by default, no cross-site tracking).
 *
 * Lives outside layout.tsx so the server component stays lean.
 */

import { useEffect } from 'react'

declare global {
  interface Window {
    posthog?: {
      init: (key: string, config: Record<string, unknown>) => void
      capture: (event: string, props?: Record<string, unknown>) => void
      identify: (id: string, props?: Record<string, unknown>) => void
      opt_out_capturing?: () => void
    }
    Sentry?: {
      onLoad: (cb: () => void) => void
      init: (config: Record<string, unknown>) => void
    }
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const target = name + '='
  const parts = document.cookie.split(';')
  for (let p of parts) {
    p = p.trim()
    if (p.startsWith(target)) return decodeURIComponent(p.slice(target.length))
  }
  return null
}

export function AnalyticsLoader() {
  useEffect(() => {
    // ── PostHog ─────────────────────────────────────────────────────
    const phKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const phHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com'
    const consent = readCookie('na_cookie_consent')
    const analyticsAllowed = consent === 'accepted'

    if (phKey && analyticsAllowed && !window.posthog) {
      try {
        // Minimal snippet — pulls the full SDK from the PostHog CDN.
        const script = document.createElement('script')
        script.async = true
        script.src = `${phHost}/static/array.js`
        script.onload = () => {
          try {
            window.posthog?.init(phKey, {
              api_host: phHost,
              capture_pageview: true,
              capture_pageleave: true,
              disable_session_recording: true, // opt-in only
              persistence: 'localStorage+cookie',
              loaded: (ph: { opt_out_capturing?: () => void }) => {
                // Guard: if the user rejects consent later, honour it.
                if (readCookie('na_cookie_consent') !== 'accepted') {
                  ph.opt_out_capturing?.()
                }
              },
            })
          } catch (e) {
            // Don't let analytics errors crash the page.
            // eslint-disable-next-line no-console
            console.warn('[analytics] posthog init failed', e)
          }
        }
        document.head.appendChild(script)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[analytics] posthog loader failed', e)
      }
    }

    // ── Sentry (browser loader snippet) ─────────────────────────────
    // Sentry's loader script is a single <script> tag with the DSN
    // embedded in the src. Users can grab their loader URL from the
    // Sentry project settings. We just inject it if the env var is
    // present. We avoid loading the full SDK at boot — the loader
    // lazy-inits on first error.
    const sentryLoaderUrl = process.env.NEXT_PUBLIC_SENTRY_LOADER_URL
    if (sentryLoaderUrl && !window.Sentry) {
      try {
        const s = document.createElement('script')
        s.async = true
        s.src = sentryLoaderUrl
        s.crossOrigin = 'anonymous'
        s.onload = () => {
          try {
            window.Sentry?.onLoad(() => {
              window.Sentry?.init({
                tracesSampleRate: 0.05,
                replaysSessionSampleRate: 0.0,
                replaysOnErrorSampleRate: 0.0,
                environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || 'production',
              })
            })
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[analytics] sentry init failed', e)
          }
        }
        document.head.appendChild(s)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[analytics] sentry loader failed', e)
      }
    }
  }, [])

  return null
}

export default AnalyticsLoader
